import { useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/components/ui/card";
import { cn } from "@/ui/lib/utils";
import {
  Crosshair,
  Shield,
  Cloud,
  Layers,
  Keyboard,
  Check,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetClassificationTaxonomy } from "@/ml-exports/api/queries/taxonomy.queries";
import {
  buildPipelineGroups,
  FAMILY_SHORTCUTS,
  getMemberDisplayOrder,
  QUALITY_GATE_PASS_VALUES,
  type PipelineGroupKey,
} from "@/ml-exports/utils/pipeline-groups";
import type { TaxonomyFamily } from "@/ml-exports/types/taxonomy";
import { TaxonomyMemberTable } from "./taxonomy-member-table";
import { TaxonomyOtherLabels } from "./taxonomy-other-labels";
import { TaxonomyTabSkeleton } from "./taxonomy-tab-skeleton";
import { TaxonomyVersionBadges } from "./taxonomy-version-badges";

/* ──────────────────────────────────────────────────────────────────
 * ClassificationTaxonomyTab — read-only reference for the v2
 * classification taxonomy: families, members, pipeline groups,
 * keyboard shortcuts and quality-gate logic. Pipeline groups,
 * shortcuts and the gate are frontend overlays keyed by family key;
 * families the overlay doesn't know about render under "Ungrouped".
 * ────────────────────────────────────────────────────────────────── */

const GROUP_VISUALS: Record<
  PipelineGroupKey,
  {
    icon: React.ComponentType<{ className?: string }>;
    border: string;
    text: string;
  }
> = {
  core: {
    icon: Crosshair,
    border: "border-l-cyan-500",
    text: "text-cyan-600 dark:text-cyan-400",
  },
  quality_gate: {
    icon: Shield,
    border: "border-l-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
  },
  environment: {
    icon: Cloud,
    border: "border-l-zinc-500/50",
    text: "text-muted-foreground",
  },
  ungrouped: {
    icon: Layers,
    border: "border-l-zinc-500/50",
    text: "text-muted-foreground",
  },
};

/** Pipeline group titles and blurbs live in the ml-exports overlay in English;
 * settings renders its own translated copy keyed by the same group key. */
type GroupTitleKey =
  | "pipelineGroups.titles.core"
  | "pipelineGroups.titles.quality_gate"
  | "pipelineGroups.titles.environment"
  | "pipelineGroups.titles.ungrouped";

type GroupDescriptionKey =
  | "pipelineGroups.descriptions.core"
  | "pipelineGroups.descriptions.quality_gate"
  | "pipelineGroups.descriptions.environment"
  | "pipelineGroups.descriptions.ungrouped";

const GROUP_TITLE_KEYS: Record<PipelineGroupKey, GroupTitleKey> = {
  core: "pipelineGroups.titles.core",
  quality_gate: "pipelineGroups.titles.quality_gate",
  environment: "pipelineGroups.titles.environment",
  ungrouped: "pipelineGroups.titles.ungrouped",
};

const GROUP_DESCRIPTION_KEYS: Record<PipelineGroupKey, GroupDescriptionKey> = {
  core: "pipelineGroups.descriptions.core",
  quality_gate: "pipelineGroups.descriptions.quality_gate",
  environment: "pipelineGroups.descriptions.environment",
  ungrouped: "pipelineGroups.descriptions.ungrouped",
};

const FamilyCard = ({
  family,
  groupKey,
  groupTitle,
}: {
  family: TaxonomyFamily;
  groupKey: PipelineGroupKey;
  groupTitle: string;
}) => {
  const { t } = useTranslation("settings");
  const shortcut = FAMILY_SHORTCUTS[family.key];

  return (
    <Card className={cn("border-l-2", GROUP_VISUALS[groupKey].border)}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-sm">{family.display_name}</CardTitle>
            <CardDescription className="font-mono text-[11px]">
              {t("classificationTaxonomy.familyMeta", {
                key: family.key,
                group: groupTitle,
              })}
            </CardDescription>
          </div>
          {shortcut && (
            <kbd className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded bg-muted border border-border text-[11px] font-mono font-semibold uppercase shrink-0">
              {shortcut}
            </kbd>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <TaxonomyMemberTable
          members={family.members ?? []}
          order={getMemberDisplayOrder(family)}
        />
      </CardContent>
    </Card>
  );
};

export const ClassificationTaxonomyTab = () => {
  const { t } = useTranslation("settings");
  const { data: taxonomy, isLoading } = useGetClassificationTaxonomy();

  const groups = useMemo(() => buildPipelineGroups(taxonomy), [taxonomy]);

  const shortcutRows = useMemo(
    () =>
      groups.flatMap((group) =>
        group.families
          .filter((family) => FAMILY_SHORTCUTS[family.key])
          .map((family) => ({
            groupKey: group.key,
            shortcut: FAMILY_SHORTCUTS[family.key],
            family: family.display_name,
          })),
      ),
    [groups],
  );

  if (isLoading || !taxonomy) return <TaxonomyTabSkeleton />;

  return (
    <div className="space-y-6">
      <TaxonomyVersionBadges
        schemaVersion={taxonomy.schema_version}
        version={taxonomy.version}
      />

      {/* ─── Pipeline groups overview ────────────────────────── */}
      <div>
        <h2 className="text-sm font-semibold mb-3">
          {t("classificationTaxonomy.pipelineGroupsTitle")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {groups.map((group) => {
            const visuals = GROUP_VISUALS[group.key];
            const Icon = visuals.icon;
            return (
              <Card key={group.key} className={cn("border-l-2", visuals.border)}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <Icon className={cn("h-4 w-4", visuals.text)} />
                    {t(GROUP_TITLE_KEYS[group.key])}
                  </CardTitle>
                  <CardDescription className="font-mono text-[11px]">
                    {group.key}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-1.5">
                  <p className="text-xs text-muted-foreground">
                    {t(GROUP_DESCRIPTION_KEYS[group.key])}
                  </p>
                  <p className="text-[11px] font-mono text-muted-foreground/80">
                    {group.families.map((f) => f.key).join(", ")}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ─── Families by pipeline group ──────────────────────── */}
      {groups.map((group) => {
        const visuals = GROUP_VISUALS[group.key];
        const Icon = visuals.icon;
        return (
          <div key={group.key}>
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
              <Icon className={cn("h-4 w-4", visuals.text)} />
              {t(GROUP_TITLE_KEYS[group.key])}
              <span className="text-[11px] font-normal text-muted-foreground">
                ·{" "}
                {t("classificationTaxonomy.familyCount", {
                  count: group.families.length,
                })}
              </span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {group.families.map((family) => (
                <FamilyCard
                  key={family.key}
                  family={family}
                  groupKey={group.key}
                  groupTitle={t(GROUP_TITLE_KEYS[group.key])}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* ─── Keyboard shortcuts reference ────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-1.5">
            <Keyboard className="h-4 w-4 text-muted-foreground" />
            {t("classificationTaxonomy.shortcutsTitle")}
          </CardTitle>
          <CardDescription>
            {t("classificationTaxonomy.shortcutsDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="font-semibold pb-1.5">
                  {t("classificationTaxonomy.columnGroup")}
                </th>
                <th className="font-semibold pb-1.5">
                  {t("classificationTaxonomy.columnShortcut")}
                </th>
                <th className="font-semibold pb-1.5">
                  {t("classificationTaxonomy.columnFamily")}
                </th>
              </tr>
            </thead>
            <tbody>
              {shortcutRows.map((row) => (
                <tr
                  key={`${row.groupKey}-${row.shortcut}`}
                  className="border-t border-border/40"
                >
                  <td
                    className={cn(
                      "py-1.5 font-medium",
                      GROUP_VISUALS[row.groupKey].text,
                    )}
                  >
                    {t(GROUP_TITLE_KEYS[row.groupKey])}
                  </td>
                  <td className="py-1.5">
                    <kbd className="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded bg-muted border border-border text-[10px] font-mono font-semibold uppercase">
                      {row.shortcut}
                    </kbd>
                  </td>
                  <td className="py-1.5">{row.family}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ─── Quality gate explanation ────────────────────────── */}
      <Card className="border-l-2 border-l-emerald-500">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-1.5">
            <Shield className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            {t("classificationTaxonomy.gateTitle")}
          </CardTitle>
          <CardDescription>
            {t("classificationTaxonomy.gateDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <table className="w-full max-w-sm text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="font-semibold pb-1.5">
                  {t("classificationTaxonomy.gateColumnFamily")}
                </th>
                <th className="font-semibold pb-1.5">
                  {t("classificationTaxonomy.gateColumnPassValue")}
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(QUALITY_GATE_PASS_VALUES).map(
                ([familyKey, passValue]) => (
                  <tr key={familyKey} className="border-t border-border/40">
                    <td className="py-1.5 font-medium">
                      {taxonomy.families.find((f) => f.key === familyKey)
                        ?.display_name ?? familyKey}
                    </td>
                    <td className="py-1.5">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 font-mono text-[11px] text-emerald-600 dark:text-emerald-400">
                        {passValue}
                      </span>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
          <ul className="space-y-1 text-xs text-muted-foreground">
            <li className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              {t("classificationTaxonomy.gatePass")}
            </li>
            <li className="flex items-center gap-1.5">
              <X className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              {t("classificationTaxonomy.gateBlocked")}
            </li>
            <li className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-zinc-400 shrink-0 mx-0.5" />
              {t("classificationTaxonomy.gateUnknown")}
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* ─── Other labels ────────────────────────────────────── */}
      <TaxonomyOtherLabels labels={taxonomy.other_labels} />
    </div>
  );
};

export default ClassificationTaxonomyTab;
